import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Typography, Button, Drawer, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { EditOutlined } from '@mui/icons-material';
import { Form2 } from '../../primitives/Forms2';
import { FormGrid, JsonEditorTextarea, LoadingIndicator, OccupyFreeSpace, SaveButton, SimpleDialog, TitleLink } from '../../primitives';
import { useSaveable } from '../../primitives/useSaveable';
import { useCrudItem } from '../../../api/useSimpleCrud';
import { useEditItem2 } from '../../../api/useNewItem';
import { CalculatorConfig } from '../types';
import { CalculatorEditor } from './CalculatorEditor';
import { CalculatorRunner } from './CalculatorRunner';
import { useCalculatorRunnerState } from '../useCalculatorRunnerState';
import { CalculatorConfigRecord } from '../storage/useCalculatorConfigsList';

interface Props {
  apiPath: string;
  pagePath?: string;
}

const emptyConfig: CalculatorConfigRecord = {
  _id: '',
  title: 'Калькулятор',
  description: '',
  inputs: [],
  rules: [],
  outputs: [],
};

export const CalculatorConfigEditPage = ({ apiPath, pagePath }: Props) => {
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const dataIn = useCrudItem<CalculatorConfigRecord>(`${apiPath}/${id}`, {
    defaultValue: emptyConfig,
  });

  const config: CalculatorConfig = {
    _id: dataIn.data._id || id,
    title: dataIn.data.title || '',
    description: dataIn.data.description,
    description_more: dataIn.data.description_more,
    inputs: dataIn.data.inputs || [],
    rules: dataIn.data.rules || [],
    outputs: dataIn.data.outputs,
  };

  const handleSave = async () => {
    await dataIn.save();
    enqueueSnackbar('Saved!', { variant: 'success' });
  };

  useSaveable({
    hasChanges: dataIn.hasChanges,
    save: handleSave,
  });

  const [isRunnerOpen, setIsRunnerOpen] = useState(false);
  const { inputValues, updateInputValues } = useCalculatorRunnerState(config);

  const handleOpenRunner = () => {
    setIsRunnerOpen(true);
  };

  const jsonEditor = useEditItem2<{ text: string }>({
    save: async (item) => {
      try {
        const parsed = JSON.parse(item.text);
        const {
          _id,
          created_at,
          created_by,
          updated_at,
          updated_by,
          ...cleanData
        } = parsed;

        dataIn.update(cleanData);
        await dataIn.save(cleanData);
        enqueueSnackbar('Saved!', { variant: 'success' });
        return item;
      } catch (err: any) {
        enqueueSnackbar(err.message || 'Invalid JSON', { variant: 'error' });
        throw err;
      }
    },
  });

  const handleConfigChange = (newConfig: CalculatorConfig) => {
    dataIn.update({
      title: newConfig.title,
      description: newConfig.description,
      description_more: newConfig.description_more,
      inputs: newConfig.inputs,
      rules: newConfig.rules,
      outputs: newConfig.outputs,
    });
  };

  return (
    <Form2
      noTitle
      background="panel"
      fitFullHeight
      formBodyProps={{
        style: { padding: 0, overflow: 'hidden' },
      }}
      headerItems={
        <>
          <Typography variant="h4">
            <TitleLink to={pagePath || '/calculators'}>
              {config.title || 'Калькулятор'}
            </TitleLink>
          </Typography>
          <OccupyFreeSpace />
          {dataIn.isLoading && <LoadingIndicator sizeVariant="s" />}
          <IconButton
            size="small"
            color="primary"
            title="Edit JSON"
            onClick={() => {
              const { _id, created_at, created_by, updated_at, updated_by, ...exportable } = dataIn.data;
              jsonEditor.startEditing({ text: JSON.stringify(exportable, null, 2) });
            }}
            sx={{ mr: 1 }}
          >
            <EditOutlined />
          </IconButton>
          <Button
            variant="text"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={handleOpenRunner}
            sx={{ mr: 1 }}
          >
            Показать калькулятор
          </Button>
          {dataIn.hasChanges && <SaveButton action={handleSave} />}
        </>
      }
    >
      <Box sx={{ height: '100%', overflow: 'hidden' }}>
        <CalculatorEditor config={config} onChange={handleConfigChange} columns columnBorders noTitle />
      </Box>

      <SimpleDialog
        isOpen={jsonEditor.isEditing}
        close={() => jsonEditor.cancel()}
        dialogTitle="JSON"
        maxWidth="lg"
        submitOnModEnter
        save={jsonEditor.save}
      >
        <FormGrid columns="1fr">
          <JsonEditorTextarea
            value={jsonEditor.item?.text || ''}
            onChange={(e) => jsonEditor.update({ text: e.target.value })}
          />
        </FormGrid>
      </SimpleDialog>

      <Drawer
        anchor="right"
        open={isRunnerOpen}
        onClose={() => setIsRunnerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480, md: 540 },
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}>
          <Typography variant="h6">{config.title || 'Калькулятор'}</Typography>
          <IconButton onClick={() => setIsRunnerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          <CalculatorRunner
            config={config}
            inputValues={inputValues}
            updateInputValues={updateInputValues}
            noTitle
          />
        </Box>
      </Drawer>
    </Form2>
  );
};
